import React from 'react'

import { Box, Icon, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { CanBulkCreateContractsTaxonomiesDocument } from 'generated/resources/graphql'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DurationInput from 'modules/core/components/DurationInput'
import type { Params } from 'modules/core/components/Inputs/AutoSaveInput'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import compensationsCollection from 'modules/contracts/store/collections/compensations'
import Compensation from 'modules/contracts/store/models/Compensation'
import AmountInputIndicator from 'modules/payroll_bundle/components/Handoffs/Supplements/Components/AmountInputIndicator'
import TaxonomyDropdown from 'modules/payroll_bundle/components/Handoffs/Supplements/Components/TaxonomyDropdown'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { useSupplementExtraData } from 'modules/payroll_bundle/components/Supplements/supplementExtraDataContext'
import { useFetchTimeBlockData } from 'modules/payroll_bundle/hooks/useFetchTimeBlockData'
import {
  ATTENDANCE_COMPENSATION_SOURCE_TYPE,
  TIMEBLOCK_SOURCE_TYPE,
} from 'modules/payroll_bundle/lib/constants'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { getPercentage } from 'modules/payroll_bundle/lib/percentage'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { roundToDecimals } from 'modules/payroll_bundle/lib/roundToDecimals'
import { isSupplementFormInvalid } from 'modules/payroll_bundle/pages/SupplementsPage/SupplementsInsights'
import Supplement, {
  decimalHoursToFullMinutes,
  minutesToDecimalHours,
} from 'modules/payroll_bundle/store/models/Supplement'
import DatevSupplementSyncStatus from 'modules/payroll_integrations_datev_api/components/SupplementSyncStatus'
import { CustomTimeRangeCategory } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

import CalculatorPopover from './CalculatorPopover'
import ControlInput from './ControlInput'
import SupplementPopoverMenu from './SupplementPopoverMenu'
import SupplementValidationButton from './SupplementValidations/SupplementValidationButton'
import TooltipContent from './TooltipContent'
import * as styles from './index.vanilla.css'

export const CURRENCY_SUB_UNIT = 100

export const labelCurrency = (unit: string, form: SupplementForm): string => {
  switch (unit) {
    case Supplement.units.money:
      return form.employee && employeeLegalEntity(form.employee).attributes.currency
    case Supplement.units.units:
      return i18n.t(`handoffs.supplements.units.units`)
    case Supplement.units.time:
      return i18n.t(`handoffs.supplements.units.time`)
    default:
      return ''
  }
}

interface Props {
  form: SupplementForm
  readonly: boolean
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  itemToHighlight?: { supplementId: number } | null
  onSubmit: () => Promise<unknown>
  onDelete: () => Promise<unknown>
  hover: boolean
  toggleTaxonomyCreationModal: (value: boolean) => void
}

const SupplementLineForm = ({
  form,
  readonly,
  store,
  taxonomies,
  itemToHighlight,
  onSubmit,
  onDelete,
  hover,
  toggleTaxonomyCreationModal,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [openObservationNote, toggleObservationNote] = useToggle(false)
  const [isObservationModalOpen, setOpenObservationNoteModal] = React.useState(false)

  const [percentage, setPercentage] = React.useState(0)
  const [isChangeFromAmount, setIsChangeFromAmount] = React.useState(false)
  const [initialAmount, setInitialAmount] = React.useState(0)
  const { loading: loadingSupplementData } = useSupplementExtraData()
  const { data } = useQuery(CanBulkCreateContractsTaxonomiesDocument)
  const canBulkCreateContractsTaxonomies =
    !!data?.contracts._policies.contractBulkCreateAdditionalCompensationType.can

  const { mappings, customTimeRangeCategories } = useFetchTimeBlockData()

  useEffect(() => {
    const amountThisPeriod = form.get('amount_in_cents').value

    const totalUpToAmount = form.compensation?.attributes.amount / CURRENCY_SUB_UNIT

    const percentageValue = getPercentage(amountThisPeriod, totalUpToAmount)
    setPercentage(roundToDecimals(percentageValue))
    setInitialAmount(amountThisPeriod)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sourceType = form.get('source_type').value
  const sourceId = form.get('source_id').value
  const sourceExist = !!(sourceType && sourceId)
  const { currentPeriod } = usePolicyPeriods()
  const isTimeCompensation = sourceType === ATTENDANCE_COMPENSATION_SOURCE_TYPE
  const isTimeBlockCompensation = sourceType === TIMEBLOCK_SOURCE_TYPE
  const taxonomyId = form.get('contracts_taxonomy_id').value

  const timeBlockCategory = useCallback(() => {
    const categoryId = mappings.find((s) => s.supplementTaxonomyId === taxonomyId)?.reimbursableId
    if (categoryId === undefined) return null

    const categoryIdNum = Number(categoryId)
    const category = customTimeRangeCategories.find((c) => c.id === categoryIdNum)
    if (category === undefined) return null

    return new CustomTimeRangeCategory(category)
  }, [taxonomyId, mappings, customTimeRangeCategories])

  const supplementIsPending = () => {
    const isSupplementInvalid = isSupplementFormInvalid(form)

    return isSupplementInvalid
      ? i18n.t('payroll.period.variable_compensations.errors.missing_amount_or_taxonomy')
      : null
  }

  const errorMessage = () => {
    if (form.isValid && !form.get('contracts_taxonomy_id').value) {
      return i18n.t('payroll.period.variable_compensations.errors.missing_taxonomy')
    }

    return null
  }

  const buildReadOnlyFieldsMap = () => {
    const compensationId = form.get('contracts_compensation_id').value

    if (compensationId) {
      const compensationCollectionItem = compensationsCollection.get(parseInt(compensationId, 10))

      if (compensationCollectionItem) {
        const isAmountReadOnly = compensationCollectionItem.isAmountReadOnly()

        return {
          unit: true,
          taxonomy: true,
          amount: isAmountReadOnly || readonly,
        }
      }
    }

    const disabled =
      !!form.get('supplement_bulk_import_id').value ||
      sourceExist ||
      isTimeCompensation ||
      isTimeBlockCompensation

    return {
      unit: readonly || form.hasVariable || disabled,
      taxonomy: readonly || disabled,
      amount: readonly || disabled,
    }
  }

  const readOnly = buildReadOnlyFieldsMap()
  const supplementId = Number(form.get('id').value)
  const policyPeriodId = currentPeriod?.attributes.id
  const policyPeriodStatus = currentPeriod?.attributes.status
  const canManageSupplement = !!supplementId || !!form.get('contracts_compensation_id').value

  const supplementValidationForm = store.supplementValidationFor(form.employee, supplementId)

  function updatePercentageAndAmount(value: number) {
    const totalUpToAmount = form.compensation?.attributes.amount / CURRENCY_SUB_UNIT
    const percentageValue = getPercentage(value, totalUpToAmount)
    setPercentage(roundToDecimals(percentageValue))
    setIsChangeFromAmount(true)
    setInitialAmount(value)
  }

  const onAmountChange = (field: Field) => {
    updatePercentageAndAmount(field.value)
  }

  const shouldFlash = itemToHighlight && supplementId === itemToHighlight.supplementId
  const supplementObservationForm = store.supplementObservationFor(supplementId)

  const observationAvailable =
    Boolean(supplementObservationForm) ||
    !!supplementObservationForm?.get('observation_text').originalValue

  const observationText = supplementObservationForm?.get('text').originalValue

  const statusInCalculationOrPaid =
    policyPeriodStatus === PayrollStatus.PAYMENT_PROCESSING ||
    policyPeriodStatus === PayrollStatus.PAID ||
    policyPeriodStatus === PayrollStatus.PAYROLL_CALCULATION
  const zeroUpToAmount = 0

  const openNoteModal = () => {
    setOpenObservationNoteModal(true)
  }

  const closeNoteModal = () => {
    setOpenObservationNoteModal(false)
  }

  const onSubmitModified = async () => {
    if (!form.data().contracts_compensation_id && Number(form.get('amount_in_cents').value) === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('payroll.period.variable_compensations.validation.incorrect_amount'),
      })

      form.get('amount_in_cents').set(form.get('amount_in_cents').originalValue)
      return
    }

    if (form.get('amount_in_cents').value !== '') {
      await onSubmit()
    }
  }

  return (
    <Box
      gap="s16"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      className={shouldFlash ? styles.flash : undefined}
    >
      <Box
        flexDirection="row"
        gap="s16"
        alignItems="center"
        aria-label={i18n.t('payroll_bundle.supplements.supplement_detail_aria_label')}
      >
        <Box flexDirection="row" gap="s8" alignItems="center" flexNoShrink>
          <DatevSupplementSyncStatus
            supplementId={supplementId}
            policyPeriodId={policyPeriodId}
            policyPeriodStatus={policyPeriodStatus}
            hover={hover}
          />
          <Box flexDirection="row" gap="s12">
            <Box width="s140">
              <ControlInput onSubmit={onSubmit} isSelect>
                {(params: Params) => (
                  <Select
                    {...params}
                    field={form.get('unit')}
                    readonly={readOnly.unit}
                    showOriginalLabelText
                    options={Object.keys(Supplement.units).map((value) => ({
                      value,
                      label: labelCurrency(value, form),
                    }))}
                  />
                )}
              </ControlInput>
            </Box>
            <Box minWidth="s200" width="s200">
              <ControlInput onSubmit={onSubmitModified}>
                {(params: Params) => {
                  if (form.get('unit').value === Supplement.units.time)
                    return (
                      <DurationInput
                        {...params}
                        readonly={readOnly.amount}
                        // Note that
                        //     form.get('amount_in_cents').value
                        // is not the `amount_in_cents` from the backend, but
                        //     amount_in_cents / 100.0
                        // That's because it has the factorial-form type 'cents'.
                        // See `SupplementForm` and
                        // https://github.com/factorialco/factorial-form/blob/master/src/Field.ts#L53-L55
                        minutes={decimalHoursToFullMinutes(form.get('amount_in_cents').value)}
                        onChange={(value) =>
                          // String conversion is important, otherwise 0 would be
                          // considered invalid (see SupplementForm's isValid) and
                          // would not be saved.
                          form.get('amount_in_cents').set(String(minutesToDecimalHours(value)))
                        }
                      />
                    )
                  return (
                    <TextInput
                      {...params}
                      readonly={readOnly.amount}
                      field={form.get('amount_in_cents')}
                      isClickableIcon
                      mandatory
                      onChange={onAmountChange}
                      placeholder={
                        form.compensationType === Compensation.types.up_to &&
                        statusInCalculationOrPaid &&
                        form.get('amount_in_cents').value.length === 0
                          ? zeroUpToAmount
                          : form.get('description').value
                      }
                      indicator={
                        <Box flexDirection="row" alignItems="center" gap="s8">
                          <AmountInputIndicator
                            form={form}
                            isCalculationOrPaid={statusInCalculationOrPaid}
                            percentageNumber={percentage}
                          />
                          <CalculatorPopover
                            form={form}
                            onSubmit={onSubmit}
                            percentage={percentage}
                            setPercentage={setPercentage}
                            initialAmount={initialAmount}
                            setInitialAmount={setInitialAmount}
                            isChangeFromAmount={isChangeFromAmount}
                            setIsChangeFromAmount={setIsChangeFromAmount}
                            hover={hover}
                          />{' '}
                        </Box>
                      }
                    />
                  )
                }}
              </ControlInput>
            </Box>
            <Box width="s200">
              <TaxonomyDropdown
                form={form}
                isReadOnly={readOnly.taxonomy}
                taxonomies={taxonomies}
                canBulkCreateContractsTaxonomies={canBulkCreateContractsTaxonomies}
                onSubmit={onSubmit}
                toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
              />
            </Box>
          </Box>
          {!errorMessage() && !hover && <Icon.Medium icon="info-circle-line" color="grey600" />}
          {!errorMessage() && hover && (
            <Tooltip.ItemList attachment="bottom center" open={open} toggle={toggle} size="normal">
              <Icon.Medium icon="info-circle-line" color="grey600" />
              <TooltipContent
                form={form}
                category={isTimeBlockCompensation ? timeBlockCategory() : null}
              />
            </Tooltip.ItemList>
          )}

          {!hover && observationAvailable && observationText && (
            <Box aria-label="supplement note">
              <Icon.Medium icon="comment" color="teal1000" />
            </Box>
          )}

          {hover && observationAvailable && observationText && (
            <Tooltip.ItemList
              key={supplementId}
              open={openObservationNote}
              toggle={toggleObservationNote}
              attachment="bottom left"
            >
              <Box onClick={openNoteModal} aria-label="supplement note">
                <Icon.Medium icon="comment" color="teal1000" />
              </Box>

              <Box textAlign="left">
                <Text.Normal size="200" lineClamp={4} overflowWrap>
                  {observationText}
                </Text.Normal>
              </Box>
            </Tooltip.ItemList>
          )}
        </Box>

        {supplementIsPending() && (
          <Box maxWidth="fit-content">
            <Tag size="normal" type="champagne">
              {supplementIsPending()}
            </Tag>
          </Box>
        )}
      </Box>
      <Box gap="s8" flexDirection="row" alignItems="center" flexNoShrink>
        {canManageSupplement && !loadingSupplementData && (
          <SupplementValidationButton
            store={store}
            supplementForm={form}
            employee={form.employee}
            form={supplementValidationForm}
            onSupplementFormSubmit={onSubmit}
          />
        )}
        <SupplementPopoverMenu
          isObservationModalOpen={isObservationModalOpen}
          openNoteModal={openNoteModal}
          closeNoteModal={closeNoteModal}
          employee={form.employee}
          supplementObservationForm={store.supplementObservationFor(supplementId)}
          supplementForm={form}
          onSupplementFormSubmit={onSubmit}
          store={store}
          taxonomies={taxonomies}
          onDelete={onDelete}
          isSupplementReadOnly={readOnly.taxonomy}
          policyPeriodStatus={policyPeriodStatus}
          hover={hover}
        />
      </Box>
    </Box>
  )
}

export default observer(SupplementLineForm)
